import {Role, SignalingClient} from "amazon-kinesis-video-streams-webrtc";
import * as AWS from "aws-sdk/global";
import KinesisVideoSignalingChannels from "aws-sdk/clients/kinesisvideosignalingchannels";
import {getId, isStudent} from "../../utils/userUtility";
import {kinesisVideoClient} from "../../utils/awsUtility";
import DailyIframe from '@daily-co/daily-js';
import request from "../../utils/request";
import {VIDEO} from "../../constants/urlConstants";
import {isMobile} from "../../utils/screenUtility";


const region = process.env.REACT_APP_AWS_REGION;

class VideoWebrtcManager {
    viewer = {
        makingOffer: false,
        ignoreOffer: false,
        msgs: []
    };

    getOrCreateChannel = async (channelId) => {
        let channelARN;
        let currentChannel;
        try {
            currentChannel = await kinesisVideoClient.describeSignalingChannel({ChannelName: channelId}).promise();
        } catch (err) {
        }
        if (!currentChannel || !currentChannel.ChannelInfo || !currentChannel.ChannelInfo.ChannelARN) {
            currentChannel = await kinesisVideoClient.createSignalingChannel({
                ChannelName: channelId,
                ChannelType: "SINGLE_MASTER",
                SingleMasterConfiguration: {
                    MessageTtlSeconds: 60
                }
            }).promise();
            channelARN = currentChannel.ChannelARN;
        } else {
            channelARN = currentChannel.ChannelInfo.ChannelARN;
        }
        return channelARN;
    }

    makePeerConnection = async (channelARN) => {
        // Get signaling channel endpoints
        const getSignalingChannelEndpointResponse = await kinesisVideoClient
            .getSignalingChannelEndpoint({
                ChannelARN: channelARN,
                SingleMasterChannelEndpointConfiguration: {
                    Protocols: ['WSS', 'HTTPS'],
                    Role: Role.VIEWER,
                },
            })
            .promise();
        const endpointsByProtocol = getSignalingChannelEndpointResponse.ResourceEndpointList.reduce((endpoints, endpoint) => {
            endpoints[endpoint.Protocol] = endpoint.ResourceEndpoint;
            return endpoints;
        }, {});
        console.log('[this.viewer] Endpoints: ', endpointsByProtocol);

        const kinesisVideoSignalingChannelsClient = new KinesisVideoSignalingChannels({
            region: region,
            credentials: AWS.config.credentials,
            endpoint: endpointsByProtocol.HTTPS,
            correctClockSkew: true,
        });

        // Get ICE server configuration
        const getIceServerConfigResponse = await kinesisVideoSignalingChannelsClient
            .getIceServerConfig({
                ChannelARN: channelARN,
            })
            .promise();
        const iceServers = [];
        iceServers.push({urls: `stun:stun.kinesisvideo.${region}.amazonaws.com:443`});
        getIceServerConfigResponse.IceServerList.forEach(iceServer =>
            iceServers.push({
                urls: iceServer.Uris,
                username: iceServer.Username,
                credential: iceServer.Password,
            }),
        );
        console.log('[this.viewer] ICE servers: ', iceServers);

        const configuration = {
            iceServers,
            iceTransportPolicy: 'all',
        };
        return new RTCPeerConnection(configuration);
    }

    collectStats = async (peerConnection, onStatsReport) => {
        return;
        const stats = await peerConnection.getStats();
        onStatsReport && onStatsReport(stats);
        console.log('WEBRTC STATS START')
        stats.forEach(report => {
            console.log(report);
        })
        console.log('WEBRTC STATS END')
    }

    makeSignalingClient = async (channelARN) => {
        const getSignalingChannelEndpointResponse = await kinesisVideoClient
            .getSignalingChannelEndpoint({
                ChannelARN: channelARN,
                SingleMasterChannelEndpointConfiguration: {
                    Protocols: ['WSS', 'HTTPS'],
                    Role: Role.MASTER,
                },
            })
            .promise();

        const endpointsByProtocol = getSignalingChannelEndpointResponse.ResourceEndpointList.reduce((endpoints, endpoint) => {
            endpoints[endpoint.Protocol] = endpoint.ResourceEndpoint;
            return endpoints;
        }, {});

        return new SignalingClient({
            channelARN: channelARN,
            channelEndpoint: endpointsByProtocol.WSS,
            clientId: getId(),
            role: Role.VIEWER,
            region: region,
            credentials: AWS.config.credentials,
            systemClockOffset: kinesisVideoClient.config.systemClockOffset,
        });
    }

    startPeer = () => {
        console.log('[this.viewer] signaling open');
        const {peerConnection, enhancedAudio, trackAdded, send, onClose, connectionState} = this.viewer;

        // Send any ICE candidates to the other peer
        peerConnection.addEventListener('icecandidate', ({candidate}) => {
            if (candidate) {
                // console.log('[this.viewer] Generated ICE candidate');

                // When trickle ICE is enabled, send the ICE candidates as they are generated.
                console.log('[this.viewer] Sending ICE candidate');
                send({type: 'iceCandidate', data: candidate});
                // this.viewer.signalingClient.sendIceCandidate(candidate);
            }
        });

        peerConnection.addEventListener('signalingstatechange', (e) => {
            console.log('peerConnection.signalingState', peerConnection.signalingState);
            if (peerConnection.signalingState === "closed") {
                onClose && onClose(peerConnection.signalingState);
            }
        });

        peerConnection.addEventListener('iceconnectionstatechange', () => {
            connectionState && connectionState(peerConnection.iceConnectionState);
        });

        peerConnection.addEventListener('negotiationneeded', async e => {
            console.log('[this.viewer] Negotiation Needed');
            if (peerConnection.signalingState !== "stable") {
                console.log('[this.viewer] signaling state is not stable: ', peerConnection.signalingState);
                return
            }

            try {
                this.viewer.makingOffer = true;
                let offer = await peerConnection.createOffer({offerToReceiveAudio: true, offerToReceiveVideo: true});
                await peerConnection.setLocalDescription(offer);
                if (enhancedAudio) {
                    peerConnection.localDescription.sdp = peerConnection.localDescription.sdp.replace('useinbandfec=1', 'useinbandfec=1; stereo=1; maxaveragebitrate=510000');
                }
                send({type: 'description', data: peerConnection.localDescription});
                // this.viewer.signalingClient.sendSdpOffer(peerConnection.localDescription);
            } catch (err) {
                console.log('[this.viewer] Negotiation error: ', err);
            } finally {
                this.viewer.makingOffer = false;
            }
        });

        // As remote tracks are received, add them to the remote view
        peerConnection.addEventListener('track', trackAdded);

    }
    start = async (channelId, onStatsReport, onRemoteDataMessage, trackAdded, connectionState, enhancedAudio = false, send, onClose) => {
        const channelARN = await this.getOrCreateChannel(channelId);
        console.log('[this.viewer] Channel ARN: ', channelARN);
        this.viewer.polite = !isStudent();
        this.viewer.send = send;
        const peerConnection = await this.makePeerConnection(channelARN);

        this.viewer.peerConnection = peerConnection;
        this.collectStats(peerConnection, onStatsReport)
        this.viewer.peerConnectionStatsInterval = setInterval(() => this.collectStats(peerConnection, onStatsReport), 5000);

        this.viewer.trackAdded = trackAdded;
        this.viewer.onClose = onClose;
        this.viewer.connectionState = connectionState;
        this.viewer.enhancedAudio = enhancedAudio;

        if (this.viewer.msgs) {
            this.viewer.msgs.forEach(msg => {
                this.onMessage(msg);
            })
            this.viewer.msgs = [];
        }

        this.startPeer();
        console.log('[this.viewer] start done');

        return this.viewer;
    }

    handleDescription = async (description) => {
        console.log('got description:', description.type, this.viewer.peerConnection.signalingState, this.viewer.makingOffer, this.viewer.polite)
        const offerCollision = (description.type === "offer") &&
            (this.viewer.makingOffer || this.viewer.peerConnection.signalingState !== "stable");

        this.viewer.ignoreOffer = !this.viewer.polite && offerCollision;
        if (this.viewer.ignoreOffer) {
            // that means we already prepared an offer and sent it, but for some reason we got another offer from the client
            // so we ignore the offer, and send out offer again cause we are not polite
            // if (this.viewer.peerConnection.signalingState === 'have-local-offer') {
            //     this.viewer.send({type: 'description', data: this.viewer.peerConnection.localDescription})
            // }
            console.log('ignoring offer, offer collision and we are not polite')
            return;
        }

        let interval = null;
        try {
            let i = 0;
            await new Promise(async (resolve, reject) => {
                interval = setInterval(async () => {
                    try {
                        await this.viewer.peerConnection.setRemoteDescription(description); // SRD rolls back as needed
                        clearInterval(interval);
                        resolve();
                    } catch (err) {
                        console.log("failed setting remote desc", i, err);
                        i++;
                        if (i > 3) {
                            reject(err);
                        }
                    }
                }, 500);

            })
        } catch (err) {
            console.log(err);
        } finally {
            clearInterval(interval);
        }
        if (description.type === "offer") {
            await this.viewer.peerConnection.setLocalDescription();
            // this.viewer.signalingClient.sendSdpAnswer(this.viewer.peerConnection.localDescription);
            this.viewer.send({type: 'description', data: this.viewer.peerConnection.localDescription})
        }
    }

    handleIceCandidate = async (iceCandidate) => {
        try {
            console.log('adding ice candidate');
            await this.viewer.peerConnection.addIceCandidate(iceCandidate);
        } catch (err) {
            if (!this.viewer.ignoreOffer) {
                throw err;
            }
        }
    }


    onMessage = async ({type, data}) => {
        try {
            if (!this.viewer.peerConnection) {
                console.log('storing message since no peerConnection');
                // this.viewer.msgs = this.viewer.msgs.push({type, data});
                return;
            }
            switch (type) {
                case 'description':
                    await this.handleDescription(data);
                    break;
                case 'iceCandidate':
                    await this.handleIceCandidate(data);
                    break;
            }

        } catch (err) {
            console.log(err);
        }
    }

    stop = () => {
        console.log('[this.viewer] Stopping this.viewer connection');
        this.viewer.peerConnection && this.viewer.peerConnection.close();
        this.viewer.peerConnection = null;

        clearInterval(this.viewer.peerConnectionStatsInterval);
        this.viewer.peerConnectionStatsInterval = null;

        this.viewer.signalingClient && this.viewer.signalingClient.close();
        this.viewer.signalingClient = null;

        this.viewer = {
            makingOffer: false,
            ignoreOffer: false,
            msgs: [],
            polite: !isStudent()
        };
    }

}

class DailyManager {
    callObject = null;
    room = null;
    roomId = null;

    start = async (roomId, participantEventHandler, errorEventHandler) => {
        this.roomId = roomId;
        await this.prepareRoom(roomId);

        let options = {}

        if (isMobile()) {
            options.camSimulcastEncodings = [{
                maxBitrate: 200000,
                maxFramerate: 15,
            },
            ];
        }
        this.callObject = DailyIframe.createCallObject();
        if (isMobile()) {
            this.callObject.setBandwidth({
                trackConstraints: {
                    width: {min: 640, max: 640, ideal: 640},
                    height: {min: 360, max: 360, ideal: 360},
                    maxFramerate: 15,
                },
            });
        }

        const participantEvents = [
            "participant-joined",
            "participant-updated",
            "participant-left"
        ];
        for (const event of participantEvents) {
            this.callObject.on(event, participantEventHandler);
        }

        const errorEvent = [
            "camera-error"
        ]

        for (const event of participantEvents) {
            this.callObject.on(errorEvent, errorEventHandler);
        }
        this.callObject.on('input-settings-updated', (e) => {
            console.log(e);
        });

        await this.startCamera();
        return this.callObject;
    }

    prepareRoom = async (roomId) => {
        this.room = await request(`${VIDEO}/rooms/${roomId}`, {method: 'POST'});
    }

    stop = async () => {
        if (this.callObject) {
            this.callObject.leave();
            this.callObject.destroy();
        }
        // await request(`${VIDEO}/rooms/${this.channelId}`, {method: 'DELETE'}, true);

        this.callObject = null;
        this.room = null;
    }

    joinRoom = async () => {
        if (this.room) {
            await this.callObject?.join(({url: this.room.url, token: this.room.token, userName: getId()}));
        }
    }

    startCamera = async () => {
        if (this.room) {
            await this.callObject?.startCamera(({url: this.room.url,  token: this.room.token, userName: getId()}));
        }
    }


    onMessage = () => {
        // no op
    }
}

export const
    videoWebrtcManager = new DailyManager();
